/**
 * Authentication service for Firebase and gcip-iap integration.
 * 
 * This module provides utilities for:
 * - Firebase app initialization with multi-tenant support
 * - Various sign-in methods (email/password, Google, SAML, OIDC)
 * - MFA verification and enrollment
 * - Sign-in method discovery for emails
 */

import { initializeApp, getApp } from 'firebase/app';
import {
    getAuth,
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    SAMLAuthProvider,
    OAuthProvider,
    fetchSignInMethodsForEmail,
    multiFactor,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier,
    getMultiFactorResolver,
} from 'firebase/auth';
import { CONFIG, PROVIDER_TYPES } from 'constants/constants';

/**
 * Firebase configuration object built from environment variables.
 * @type {Object}
 */
const firebaseConfig = {
    apiKey: CONFIG.FIREBASE_API_KEY,
    authDomain: `${CONFIG.FIREBASE_PROJECT_ID}.firebaseapp.com`,
    projectId: CONFIG.FIREBASE_PROJECT_ID,
};

/**
 * Cache for Firebase app instances keyed by tenant ID.
 * @type {Map<string, import('firebase/app').FirebaseApp>}
 */
const appCache = new Map();

/**
 * Gets or creates a Firebase app instance for the given tenant.
 * Uses caching to prevent duplicate initialization.
 *
 * @param {string|null} tenantId - Tenant ID or null for default project
 * @returns {import('firebase/app').FirebaseApp} Firebase app instance
 */
const getFirebaseApp = (tenantId) => {
    const appName = tenantId || '[DEFAULT]';

    if (appCache.has(appName)) {
        return appCache.get(appName);
    }

    try {
        const app = getApp(appName);
        appCache.set(appName, app);
        return app;
    } catch {
        const app = initializeApp(firebaseConfig, appName);
        appCache.set(appName, app);
        return app;
    }
};

/**
 * Gets a Firebase Auth instance configured for the given API key and tenant.
 * This is the implementation for AuthenticationHandler.getAuth().
 *
 * @param {string} apiKey - Firebase API key (validated against config)
 * @param {string|null} tenantId - Tenant ID or null for project-level auth
 * @returns {import('firebase/auth').Auth} Configured Auth instance
 * @throws {Error} If API key doesn't match configuration
 */
export const getFirebaseAuth = (apiKey, tenantId) => {
    if (apiKey !== CONFIG.FIREBASE_API_KEY) {
        throw new Error('Invalid API key configuration');
    }

    const app = getFirebaseApp(tenantId);
    const auth = getAuth(app);

    if (tenantId) {
        auth.tenantId = tenantId;
    }

    // Disable app verification for testing (allows test phone numbers to bypass reCAPTCHA)
    if (CONFIG.DISABLE_RECAPTCHA) {
        auth.settings.appVerificationDisabledForTesting = true;
    }

    return auth;
};

/**
 * Fetches available sign-in methods for an email address.
 * Used for dynamic provider detection (FR-2.1.5).
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance
 * @param {string} email - Email address to check
 * @returns {Promise<{hasPassword: boolean, hasGoogle: boolean, samlProviders: string[], oidcProviders: string[]}>}
 */
export const getSignInMethodsForEmail = async (auth, email) => {
    const methods = await fetchSignInMethodsForEmail(auth, email);

    return {
        hasPassword: methods.includes(PROVIDER_TYPES.PASSWORD),
        hasGoogle: methods.includes(PROVIDER_TYPES.GOOGLE),
        samlProviders: methods.filter(m => m.startsWith(PROVIDER_TYPES.SAML_PREFIX)),
        oidcProviders: methods.filter(m => m.startsWith(PROVIDER_TYPES.OIDC_PREFIX)),
    };
};

/**
 * Signs in a user with email and password.
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const signInWithEmail = async (auth, email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
};

/**
 * Signs in a user with Google OAuth using a popup.
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance
 * @param {string} email - Email hint for account selection
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const signInWithGoogle = async (auth, email) => {
    const provider = new GoogleAuthProvider();
    if (email) {
        provider.setCustomParameters({ login_hint: email });
    }
    return signInWithPopup(auth, provider);
};

/**
 * Signs in a user with a SAML provider using a popup.
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance
 * @param {string} providerId - SAML provider ID (e.g., 'saml.my-provider')
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const signInWithSAML = async (auth, providerId) => {
    const provider = new SAMLAuthProvider(providerId);
    return signInWithPopup(auth, provider);
};

/**
 * Signs in a user with an OIDC provider using a popup.
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance
 * @param {string} providerId - OIDC provider ID (e.g., 'oidc.my-provider')
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const signInWithOIDC = async (auth, providerId) => {
    const provider = new OAuthProvider(providerId);
    return signInWithPopup(auth, provider);
};

/**
 * Creates a reCAPTCHA verifier for phone auth operations.
 * Uses invisible reCAPTCHA (NFR-3.1.3).
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance
 * @param {string} containerId - DOM element ID for reCAPTCHA container
 * @param {Function} onExpired - Callback when reCAPTCHA expires
 * @returns {RecaptchaVerifier}
 */
export const createRecaptchaVerifier = (auth, containerId, onExpired) => {
    const verifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: () => {
            // reCAPTCHA solved - will proceed with phone verification
        },
        'expired-callback': () => {
            onExpired?.();
        },
    });

    return verifier;
};

/**
 * Gets the MFA resolver from a multi-factor error.
 * Uses Firebase's getMultiFactorResolver to properly extract resolver from error.
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance
 * @param {Error} error - Firebase auth error with MFA required
 * @returns {{resolver: import('firebase/auth').MultiFactorResolver, hints: import('firebase/auth').MultiFactorInfo[]}}
 */
export const getMFAResolver = (auth, error) => {
    if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        return {
            resolver,
            hints: resolver.hints,
        };
    }
    throw error;
};

/**
 * Sends an SMS verification code for MFA verification.
 *
 * @param {import('firebase/auth').Auth} auth - Auth instance (must match recaptchaVerifier's auth)
 * @param {import('firebase/auth').MultiFactorResolver} resolver - MFA resolver from error
 * @param {import('firebase/auth').PhoneMultiFactorInfo} hint - Phone hint from resolver
 * @param {RecaptchaVerifier} recaptchaVerifier - reCAPTCHA verifier
 * @returns {Promise<string>} Verification ID for completing MFA
 */
export const sendMFAVerificationCode = async (auth, resolver, hint, recaptchaVerifier) => {
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        recaptchaVerifier
    );
    return verificationId;
};

/**
 * Completes MFA verification with the code entered by user.
 *
 * @param {import('firebase/auth').MultiFactorResolver} resolver - MFA resolver
 * @param {string} verificationId - ID returned from sendMFAVerificationCode
 * @param {string} verificationCode - 6-digit code from SMS
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const completeMFAVerification = async (resolver, verificationId, verificationCode) => {
    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    return resolver.resolveSignIn(multiFactorAssertion);
};

/**
 * Checks if a user has MFA enrolled.
 *
 * @param {import('firebase/auth').User} user - Firebase user
 * @returns {boolean}
 */
export const hasUserEnrolledMFA = (user) => {
    return multiFactor(user).enrolledFactors.length > 0;
};

/**
 * Gets the phone number hint for MFA enrollment from user's profile.
 *
 * @param {import('firebase/auth').User} user - Firebase user
 * @returns {string|null} Phone number or null if not available
 */
export const getUserPhoneForMFA = (user) => {
    // Check for phone number in user profile
    if (user.phoneNumber) {
        return user.phoneNumber;
    }

    // Check enrolled factors for existing phone
    const factors = multiFactor(user).enrolledFactors;
    const phoneFactor = factors.find(f => f.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
    if (phoneFactor?.phoneNumber) {
        return phoneFactor.phoneNumber;
    }

    return null;
};

/**
 * Starts MFA enrollment by sending a verification code.
 *
 * @param {import('firebase/auth').User} user - Firebase user
 * @param {string} phoneNumber - Phone number for enrollment
 * @param {RecaptchaVerifier} recaptchaVerifier - reCAPTCHA verifier
 * @returns {Promise<string>} Verification ID
 */
export const startMFAEnrollment = async (user, phoneNumber, recaptchaVerifier) => {
    const session = await multiFactor(user).getSession();
    const phoneAuthProvider = new PhoneAuthProvider(user.auth);

    return phoneAuthProvider.verifyPhoneNumber(
        { phoneNumber, session },
        recaptchaVerifier
    );
};

/**
 * Completes MFA enrollment with verification code.
 *
 * @param {import('firebase/auth').User} user - Firebase user
 * @param {string} verificationId - ID from startMFAEnrollment
 * @param {string} verificationCode - 6-digit code from SMS
 * @returns {Promise<void>}
 */
export const completeMFAEnrollment = async (user, verificationId, verificationCode) => {
    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    await multiFactor(user).enroll(multiFactorAssertion, 'Phone Number');
};

/**
 * Masks a phone number for display (shows last 4 digits).
 *
 * @param {string} phoneNumber - Full phone number
 * @returns {string} Masked phone number (e.g., "***-***-1234")
 */
export const maskPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length < 4) return phoneNumber;
    const lastFour = cleaned.slice(-4);
    return `***-***-${lastFour}`;
};
